import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import CrisisResourceBanner from '../../components/CrisisResourceBanner';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import MetricCard from './components/MetricCard';
import PerformanceChart from './components/PerformanceChart';
import AlertPanel from './components/AlertPanel';
import ServiceHealthTable from './components/ServiceHealthTable';
import { usePerformanceMetrics } from './hooks/usePerformanceMetrics';

const SystemPerformanceDashboard = () => {
  const [timeRange, setTimeRange] = useState('30days');
  const [activeView, setActiveView] = useState('overview');
  const { realTimeData, alerts, serviceHealth, chartData, loading, error } = usePerformanceMetrics();

  const timeRangeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: '1year', label: 'Last Year' }
  ];

  const viewTabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'api', label: 'API Performance', icon: 'Zap' },
    { id: 'database', label: 'Database', icon: 'Database' },
    { id: 'sessions', label: 'Session Quality', icon: 'Video' },
    { id: 'alerts', label: 'Alerts', icon: 'AlertTriangle' }
  ];

  const metricCards = [
    {
      label: 'API Response Time',
      value: `${Math.round(realTimeData?.apiResponseTime)}ms`,
      status: realTimeData?.apiResponseTime < 200 ? 'healthy' : realTimeData?.apiResponseTime < 500 ? 'warning' : 'critical',
      icon: 'Zap',
      description: 'Average response time',
      change: realTimeData?.apiResponseTime < 200 ? '-8ms' : '+12ms',
      trend: realTimeData?.apiResponseTime < 200 ? 'down' : 'up'
    },
    {
      label: 'Database Query Time',
      value: `${Math.round(realTimeData?.dbQueryTime)}ms`,
      status: realTimeData?.dbQueryTime < 50 ? 'healthy' : realTimeData?.dbQueryTime < 100 ? 'warning' : 'critical',
      icon: 'Database',
      description: 'Average query duration',
      change: realTimeData?.dbQueryTime < 50 ? '-3ms' : '+5ms',
      trend: realTimeData?.dbQueryTime < 50 ? 'down' : 'up'
    },
    {
      label: 'Session Connection Quality',
      value: `${realTimeData?.sessionQuality?.toFixed(1)}%`,
      status: realTimeData?.sessionQuality > 90 ? 'healthy' : realTimeData?.sessionQuality > 75 ? 'warning' : 'critical',
      icon: 'Video',
      description: 'Connection quality score',
      change: realTimeData?.sessionQuality > 90 ? '+2.1%' : '-1.3%',
      trend: realTimeData?.sessionQuality > 90 ? 'up' : 'down'
    },
    {
      label: 'Active Connections',
      value: realTimeData?.activeConnections?.toString(),
      status: 'healthy',
      icon: 'Users',
      description: 'Currently active sessions',
      change: '+5',
      trend: 'up'
    }
  ];

  const apiPerformanceData = [
    { time: '00:00', responseTime: 145, requests: 234 },
    { time: '04:00', responseTime: 132, requests: 189 },
    { time: '08:00', responseTime: 178, requests: 456 },
    { time: '12:00', responseTime: 198, requests: 589 },
    { time: '16:00', responseTime: 165, requests: 512 },
    { time: '20:00', responseTime: 142, requests: 378 },
    { time: '23:59', responseTime: Math.round(realTimeData?.apiResponseTime), requests: 298 }
  ];

  const dbPerformanceData = [
    { time: '00:00', queryTime: 18, slowQueries: 2 },
    { time: '04:00', queryTime: 15, slowQueries: 1 },
    { time: '08:00', queryTime: 28, slowQueries: 5 },
    { time: '12:00', queryTime: 32, slowQueries: 8 },
    { time: '16:00', queryTime: 25, slowQueries: 4 },
    { time: '20:00', queryTime: 21, slowQueries: 3 },
    { time: '23:59', queryTime: Math.round(realTimeData?.dbQueryTime), slowQueries: 2 }
  ];

  const sessionQualityData = [
    { time: '00:00', quality: 95.2, latency: 45, packetLoss: 0.2 },
    { time: '04:00', quality: 96.1, latency: 38, packetLoss: 0.1 },
    { time: '08:00', quality: 93.5, latency: 52, packetLoss: 0.4 },
    { time: '12:00', quality: 91.8, latency: 68, packetLoss: 0.8 },
    { time: '16:00', quality: 94.3, latency: 48, packetLoss: 0.3 },
    { time: '20:00', quality: 95.8, latency: 42, packetLoss: 0.2 },
    { time: '23:59', quality: realTimeData?.sessionQuality, latency: 45, packetLoss: 0.2 }
  ];

  // Use real alerts from hook or fallback to empty array
  const activeAlerts = alerts?.length > 0 ? alerts : [];

  // Use real service health from hook
  const serviceHealthData = serviceHealth?.length > 0 ? serviceHealth : [
    {
      service: 'Authentication Service',
      status: 'operational',
      uptime: '100%',
      responseTime: '98ms',
      lastIncident: 'None'
    },
    {
      service: 'API Gateway',
      status: 'operational',
      uptime: '99.9%',
      responseTime: `${Math.round(realTimeData?.apiResponseTime)}ms`,
      lastIncident: 'None'
    },
    {
      service: 'Database Cluster',
      status: 'operational',
      uptime: '100%',
      responseTime: `${Math.round(realTimeData?.dbQueryTime)}ms`,
      lastIncident: 'None'
    },
    {
      service: 'Video Infrastructure',
      status: realTimeData?.sessionQuality > 90 ? 'operational' : 'degraded',
      uptime: '99.7%',
      responseTime: '245ms',
      lastIncident: 'None'
    },
    {
      service: 'Notification Service',
      status: 'operational',
      uptime: '99.8%',
      responseTime: '156ms',
      lastIncident: 'None'
    }
  ];

  const renderContent = () => {
    switch (activeView) {
      case 'overview':
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {metricCards?.map((metric, index) => (
                <MetricCard key={index} {...metric} />
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <PerformanceChart
                title="API Response Time Trend"
                data={apiPerformanceData}
                dataKey="responseTime"
                color="#4A90A4"
                unit="ms"
              />
              <PerformanceChart
                title="Database Query Performance"
                data={dbPerformanceData}
                dataKey="queryTime"
                color="#7B68A6"
                unit="ms"
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ServiceHealthTable services={serviceHealthData} />
              <AlertPanel alerts={activeAlerts} />
            </div>
          </>
        );
      case 'api':
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <MetricCard {...metricCards?.[0]} />
              <MetricCard
                label="Total Requests"
                value="45.2K"
                status="healthy"
                icon="Activity"
                description="Last 24 hours"
                change="+12%"
                trend="up"
              />
            </div>
            <PerformanceChart
              title="API Response Time - Detailed View"
              data={apiPerformanceData}
              dataKey="responseTime"
              color="#4A90A4"
              unit="ms"
              height={400}
            />
          </>
        );
      case 'database':
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <MetricCard {...metricCards?.[1]} />
              <MetricCard
                label="Slow Queries"
                value="12"
                status="warning"
                icon="AlertTriangle"
                description="Queries > 100ms"
                change="+3"
                trend="up"
              />
            </div>
            <PerformanceChart
              title="Database Query Performance - Detailed View"
              data={dbPerformanceData}
              dataKey="queryTime"
              color="#7B68A6"
              unit="ms"
              height={400}
            />
          </>
        );
      case 'sessions':
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <MetricCard {...metricCards?.[2]} />
              <MetricCard
                label="Average Latency"
                value="45ms"
                status="healthy"
                icon="Gauge"
                description="Connection latency"
                change="-5ms"
                trend="down"
              />
              <MetricCard
                label="Packet Loss"
                value="0.2%"
                status="healthy"
                icon="WifiOff"
                description="Average packet loss"
                change="-0.1%"
                trend="down"
              />
            </div>
            <PerformanceChart
              title="Session Connection Quality - Detailed View"
              data={sessionQualityData}
              dataKey="quality"
              color="#48BB78"
              unit="%"
              height={400}
            />
          </>
        );
      case 'alerts':
        return (
          <div className="grid grid-cols-1 gap-6">
            <AlertPanel alerts={activeAlerts} expanded />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>System Performance Dashboard - Cwtch Platform</title>
        <meta name="description" content="Real-time monitoring of API response times, database query performance, and session connection quality metrics" />
      </Helmet>
      <CrisisResourceBanner />
      <Header />
      <div className="min-h-screen bg-background pt-32">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Activity" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">System Performance Dashboard</h1>
                  <p className="text-muted-foreground">Real-time monitoring and analytics</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Select
                  options={timeRangeOptions}
                  value={timeRange}
                  onChange={setTimeRange}
                  className="w-48"
                />
                <Button variant="outline" iconName="Download">
                  Export Report
                </Button>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border mb-6">
              <div className="overflow-x-auto">
                <div className="flex border-b border-border">
                  {viewTabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveView(tab?.id)}
                      className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                        activeView === tab?.id
                          ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-background">
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default SystemPerformanceDashboard;